"use client";

import { useState } from "react";

import { Text } from "@/src/components/ui/Typography";
import { ChatGPTIcon, ClaudeIcon, GeminiIcon, GrokIcon, PerplexityIcon } from "./icons";

// buildUrl gets the same prompt text that's copied to the clipboard. A page
// can't reach into a window.open() tab pointed at a different origin to
// type into it directly (cross-origin scripting is blocked for exactly the
// reason you'd want it to be), so prefilling only works where the provider
// itself supports a query param for it. ChatGPT, Claude, Grok, and
// Perplexity all take `q`; Gemini takes `prompt` instead.
const PROVIDERS = [
  {
    name: "ChatGPT",
    Icon: ChatGPTIcon,
    buildUrl: (prompt: string) => `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`,
  },
  {
    name: "Perplexity",
    Icon: PerplexityIcon,
    buildUrl: (prompt: string) =>
      `https://www.perplexity.ai/search?q=${encodeURIComponent(prompt)}`,
  },
  {
    name: "Claude",
    Icon: ClaudeIcon,
    buildUrl: (prompt: string) => `https://claude.ai/?q=${encodeURIComponent(prompt)}`,
  },
  {
    name: "Grok",
    Icon: GrokIcon,
    buildUrl: (prompt: string) => `https://grok.com/?q=${encodeURIComponent(prompt)}`,
  },
] as const;

interface SummarizeBarPillProps {
  url: string;
}

// The actual pill UI + copy-to-clipboard logic, shared by both the sticky
// (in-column) and resting (post-grid) SummarizeBar instances so the two never
// drift out of sync visually.
export function SummarizeBarPill({ url }: SummarizeBarPillProps) {
  const prompt = `Here's URL, a concise overview from the TechEniac Team. Summarize the article's key points in a clear and easy-to-understand way for someone short on time, highlighting the main insights and practical takeaways. Source:: ${url}`;

  const summarize = async (href: string) => {
    // Deliberately no tab is opened yet — even a blank one steals focus
    // away from this page, so the reader never actually sees "Prompt
    // copied" before their view jumps elsewhere. Stay put, show the
    // confirmation, then open the tab.
    //
    // Still copied to the clipboard even for providers whose URL already
    // embeds the prompt (ChatGPT, Perplexity) — a harmless fallback if the
    // destination ever fails to pick up the query param.
    try {
      await navigator.clipboard.writeText(prompt);
    } catch {
      /* clipboard blocked - ignore */
    }

    // No "noopener" here deliberately — with it, window.open() always
    // returns null regardless of whether the popup succeeded, which made
    // the "blocked" fallback below fire every time and redirect this tab
    // too. Get a real reference instead, so blocked-vs-succeeded can
    // actually be told apart, and sever `opener` by hand afterward for the
    // same reverse-tabnabbing hardening `noopener` would have given us.
    const newTab = window.open(href, "_blank");
    if (newTab) {
      newTab.opener = null;
    } else {
      // A window.open() this long after the click can get popup-blocked in
      // some browsers (the "transient user activation" window has expired)
      // — fall back to navigating the current tab so the action still completes.
      window.location.assign(href);
    }
  };

  return (
    <div className="flex min-w-0 max-w-[calc(100vw-1rem)] flex-col items-center">
      <div className="flex items-center gap-2 rounded-full border border-neutral-100 bg-white px-3 py-2 shadow-2xl sm:gap-4 sm:px-6 sm:py-4">
        <Text as="span" size="labelSM" className="whitespace-nowrap text-neutral-700 sm:hidden">
          Summarize:
        </Text>
        <Text as="span" size="labelMD" className="hidden whitespace-nowrap text-neutral-700 sm:inline">
          Summarize with AI:
        </Text>
        <div className="flex items-center gap-1.5 sm:gap-2.5">
          {PROVIDERS.map(({ name, Icon, buildUrl }) => (
            <button
              key={name}
              type="button"
              onClick={() => summarize(buildUrl(prompt))}
              aria-label={`Summarize with ${name}`}
              title={`Summarize with ${name}`}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-neutral-50 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:scale-105 hover:shadow-md sm:h-10 sm:w-10"
            >
              <Icon />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
